/** 繰り返しタスクの定義と入力値のRepository。 */

import { getDb, type TaskEntryRecord } from './schema';
import { enqueue } from './outbox';
import { bumpData } from './events';
import { getDeviceId, newId } from '../domain/ids';
import { nowIsoUtc } from '../lib/datetime';
import {
  isTaskValueEmpty,
  SCHEMA_VERSION,
  taskEntryKey,
  type TaskDef,
  type TaskEntry,
  type TaskKind,
  type TasksDoc,
  type TaskValue,
} from '../domain/types';

const DEFS_KEY = 'tasks';

/* ---------------- 定義 ---------------- */

export function emptyTasks(): TasksDoc {
  return {
    schemaVersion: SCHEMA_VERSION,
    updatedAt: nowIsoUtc(),
    deviceId: getDeviceId(),
    tasks: [],
  };
}

export async function getTasksDoc(): Promise<TasksDoc> {
  const db = await getDb();
  const rec = await db.get('meta', DEFS_KEY);
  return (rec?.value as TasksDoc | undefined) ?? emptyTasks();
}

async function saveDefs(doc: TasksDoc): Promise<void> {
  const db = await getDb();
  await db.put('meta', {
    key: DEFS_KEY,
    value: { ...doc, updatedAt: nowIsoUtc(), deviceId: getDeviceId() },
  });
  await enqueue({ type: 'tasks.put', targetKey: 'tasks' });
  bumpData();
}

export async function putTasksFromRemote(doc: TasksDoc): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key: DEFS_KEY, value: doc });
  bumpData();
}

/** 入力欄に並べるタスク。停止中のものは含めない。 */
export function activeTasks(doc: TasksDoc, kind: TaskKind): TaskDef[] {
  return doc.tasks
    .filter((t) => t.kind === kind && !t.archived)
    .sort((a, b) => a.order - b.order);
}

export function allTasks(doc: TasksDoc): TaskDef[] {
  return [...doc.tasks].sort((a, b) =>
    a.kind === b.kind ? a.order - b.order : a.kind === 'daily' ? -1 : 1,
  );
}

export async function addTask(
  input: Omit<TaskDef, 'id' | 'order' | 'archived' | 'createdAt'>,
): Promise<void> {
  const doc = await getTasksDoc();
  const same = doc.tasks.filter((t) => t.kind === input.kind);
  await saveDefs({
    ...doc,
    tasks: [
      ...doc.tasks,
      {
        ...input,
        id: newId(),
        order: Math.max(0, ...same.map((t) => t.order)) + 1,
        archived: false,
        createdAt: nowIsoUtc(),
      },
    ],
  });
}

export async function updateTask(id: string, patch: Partial<TaskDef>): Promise<void> {
  const doc = await getTasksDoc();
  await saveDefs({
    ...doc,
    tasks: doc.tasks.map((t) => (t.id === id ? { ...t, ...patch, id: t.id } : t)),
  });
}

export async function setArchived(id: string, archived: boolean): Promise<void> {
  await updateTask(id, { archived });
}

export async function moveTask(id: string, delta: number): Promise<void> {
  const doc = await getTasksDoc();
  const target = doc.tasks.find((t) => t.id === id);
  if (!target) return;
  const list = activeTasks(doc, target.kind);
  const i = list.findIndex((t) => t.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
  const orderById = new Map(list.map((t, n) => [t.id, n + 1]));
  await saveDefs({
    ...doc,
    tasks: doc.tasks.map((t) => (orderById.has(t.id) ? { ...t, order: orderById.get(t.id)! } : t)),
  });
}

/* ---------------- 入力値 ---------------- */

function toRecord(e: TaskEntry, dirty: boolean, etag: string | null): TaskEntryRecord {
  return {
    ...e,
    key: taskEntryKey(e.kind, e.period),
    monthKey: e.kind === 'daily' ? e.period.slice(0, 7) : '',
    _dirty: dirty,
    _etag: etag,
  };
}

export function emptyEntry(kind: TaskKind, period: string): TaskEntry {
  const now = nowIsoUtc();
  return {
    schemaVersion: SCHEMA_VERSION,
    kind,
    period,
    createdAt: now,
    updatedAt: now,
    rev: 0,
    deviceId: getDeviceId(),
    values: {},
  };
}

export async function getTaskEntry(
  kind: TaskKind,
  period: string,
): Promise<TaskEntryRecord | undefined> {
  const db = await getDb();
  return db.get('taskEntries', taskEntryKey(kind, period));
}

export async function listTaskEntries(kind: TaskKind): Promise<TaskEntryRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex('taskEntries', 'by_kind', kind);
  return all.sort((a, b) => (a.period < b.period ? -1 : 1));
}

export async function listTaskEntriesByMonth(month: string): Promise<TaskEntryRecord[]> {
  const db = await getDb();
  return db.getAllFromIndex('taskEntries', 'by_month', month);
}

/** 1つのタスクの値を書き込む。空になったキーは持たない。全部空なら期間ごと削除する。 */
export async function setTaskValue(
  kind: TaskKind,
  period: string,
  taskId: string,
  value: TaskValue | undefined,
): Promise<void> {
  const db = await getDb();
  const existing = await db.get('taskEntries', taskEntryKey(kind, period));
  const base: TaskEntry = existing ?? emptyEntry(kind, period);

  const values = { ...base.values };
  if (isTaskValueEmpty(value)) delete values[taskId];
  else values[taskId] = value as TaskValue;

  if (Object.keys(values).length === 0) {
    if (existing) await deleteTaskEntry(kind, period);
    return;
  }

  const next: TaskEntry = {
    ...base,
    values,
    schemaVersion: SCHEMA_VERSION,
    deviceId: getDeviceId(),
    updatedAt: nowIsoUtc(),
    rev: (existing?.rev ?? 0) + 1,
  };
  await db.put('taskEntries', toRecord(next, true, existing?._etag ?? null));
  await enqueue({
    type: 'taskentry.upsert',
    targetKey: `taskentry:${taskEntryKey(kind, period)}`,
    taskKind: kind,
    period,
    month: kind === 'daily' ? period.slice(0, 7) : undefined,
  });
  bumpData();
}

export async function deleteTaskEntry(kind: TaskKind, period: string): Promise<void> {
  const db = await getDb();
  const key = taskEntryKey(kind, period);
  const rec = await db.get('taskEntries', key);
  if (!rec) return;
  await db.delete('taskEntries', key);
  await enqueue({
    type: 'taskentry.delete',
    targetKey: `taskentry:${key}`,
    taskKind: kind,
    period,
    month: kind === 'daily' ? period.slice(0, 7) : undefined,
  });
  bumpData();
}

export async function putTaskEntryFromRemote(e: TaskEntry, etag: string | null): Promise<void> {
  const db = await getDb();
  await db.put('taskEntries', toRecord(e, false, etag));
}
